import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { LOGO } from "@/data/exercises";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, Mail, HeartHandshake, Loader2, CheckCircle2 } from "lucide-react";

const CONTACT_EMAIL = "passerelle2026@outlook.fr";

export default function Contact() {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", { name, email, message });
      setSent(true);
      toast.success("Message envoyé ! Nous vous répondrons rapidement.");
      setName(""); setEmail(""); setMessage("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Une erreur est survenue. Réessayez.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF7F2] font-care flex flex-col">
      <header className="bg-white/70 border-b border-amber-100 sticky top-0 z-10 backdrop-blur">
        <div className="max-w-3xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO} alt="Passerelle" className="h-10 w-auto" />
            <span className="text-gray-300">·</span>
            <span className="text-gray-500 text-sm font-semibold">Contact</span>
          </div>
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="contact-back-button">
            <ArrowLeft className="w-4 h-4 mr-1" /> Retour
          </Button>
        </div>
      </header>

      <main className="flex-1 max-w-2xl w-full mx-auto px-6 py-14" data-testid="contact-content">
        <div className="text-center mb-10">
          <span className="inline-flex w-16 h-16 rounded-2xl bg-emerald-100 text-emerald-700 items-center justify-center mb-5">
            <HeartHandshake className="w-9 h-9" />
          </span>
          <h1 className="font-serif-display text-4xl sm:text-5xl font-normal text-gray-800 mb-4">Nous contacter</h1>
          <p className="text-lg text-gray-500 leading-relaxed">
            Une question, une suggestion ou besoin d'aide&nbsp;? Écrivez-nous directement ci-dessous,
            notre équipe vous répondra avec bienveillance.
          </p>
        </div>

        {sent ? (
          <div className="rounded-3xl bg-white border-4 border-emerald-700/10 p-10 text-center" data-testid="contact-success">
            <CheckCircle2 className="w-16 h-16 text-emerald-600 mx-auto mb-4" />
            <h2 className="font-serif-display text-2xl font-normal text-gray-800 mb-2">Message envoyé</h2>
            <p className="text-gray-500 mb-6">Merci&nbsp;! Nous reviendrons vers vous dès que possible.</p>
            <Button variant="outline" onClick={() => setSent(false)} data-testid="contact-send-another">
              Envoyer un autre message
            </Button>
          </div>
        ) : (
          <form onSubmit={submit} className="rounded-3xl bg-white border-4 border-emerald-700/10 p-8 sm:p-10 space-y-5" data-testid="contact-form">
            <div>
              <Label htmlFor="c-name">Votre nom</Label>
              <Input id="c-name" required value={name} onChange={(e) => setName(e.target.value)}
                placeholder="Prénom et nom" className="mt-1 h-12" data-testid="contact-name-input" />
            </div>
            <div>
              <Label htmlFor="c-email">Votre e-mail</Label>
              <Input id="c-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="vous@exemple.fr" className="mt-1 h-12" data-testid="contact-email-input" />
            </div>
            <div>
              <Label htmlFor="c-message">Votre message</Label>
              <Textarea id="c-message" required value={message} onChange={(e) => setMessage(e.target.value)}
                placeholder="Comment pouvons-nous vous aider ?" rows={6} className="mt-1" data-testid="contact-message-input" />
            </div>
            <Button type="submit" disabled={loading}
              className="w-full h-12 text-base text-white rounded-xl border-0"
              style={{ background: "#2E8EC2" }}
              data-testid="contact-submit-button">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Mail className="w-5 h-5 mr-2" /> Envoyer le message</>}
            </Button>

            <p className="text-center text-sm text-gray-400 pt-1">
              Ou écrivez-nous à{" "}
              <a href={`mailto:${CONTACT_EMAIL}`} className="text-emerald-700 font-semibold hover:underline" data-testid="contact-email-link">
                {CONTACT_EMAIL}
              </a>
            </p>
          </form>
        )}
      </main>
    </div>
  );
}
